import { z } from 'zod';
import { TRIBAL_OPTIONS, stateForZip } from '../enums.customers';
import type { TribalOption } from '../enums.customers';
import { US_STATE_CODES } from '../enums';
import type { LookupKind, RecordStatus } from '../enums';
import { listQuerySchema, optionalText, queryBool, recordStatus } from './common';
import type { UserRef } from './users';

/* ------------------------- Service Area (PC237) --------------------------- */

export const zip5 = z
  .string()
  .trim()
  .regex(/^\d{5}$/, 'ZIP code must be 5 digits');

const programsSchema = z.object({
  lifeline: z.boolean().default(false),
  nonLifeline: z.boolean().default(false),
  ebb: z.boolean().default(false),
});

/** One ZIP with optional city and state, as a row of the bulk file. */
export const serviceZipSchema = z.object({
  zip: zip5,
  city: z.string().trim().max(60).optional().default(''),
  state: z.enum(US_STATE_CODES).or(z.literal('')).optional().default(''),
});

/**
 * "Add/Update Area": opens the listed ZIP codes for the ticked programmes.
 * The reference form takes ZIP codes only; the state is derived from the ZIP.
 */
export const serviceAreaUpsertSchema = z
  .object({
    zips: z.array(serviceZipSchema).min(1, 'Enter at least one ZIP code').max(5000),
    active: z.boolean().default(true),
    tribal: z.enum(TRIBAL_OPTIONS).default('No'),
    programs: programsSchema,
  })
  .superRefine((v, ctx) => {
    if (!v.programs.lifeline && !v.programs.nonLifeline && !v.programs.ebb)
      ctx.addIssue({ code: 'custom', path: ['programs'], message: 'Tick at least one programme' });
    v.zips.forEach((z5, i) => {
      if (!z5.state && !stateForZip(z5.zip))
        ctx.addIssue({
          code: 'custom',
          path: ['zips', i, 'zip'],
          message: `ZIP ${z5.zip} does not belong to a US state`,
        });
    });
  });
export type ServiceAreaUpsertInput = z.input<typeof serviceAreaUpsertSchema>;

/** "Delete Area": closes the listed ZIP codes for the ticked programmes. */
export const serviceAreaRemoveSchema = z
  .object({
    zips: z.array(zip5).min(1, 'Enter at least one ZIP code').max(5000),
    programs: programsSchema,
  })
  .refine((v) => v.programs.lifeline || v.programs.nonLifeline || v.programs.ebb, {
    path: ['programs'],
    message: 'Tick at least one programme',
  });
export type ServiceAreaRemoveInput = z.input<typeof serviceAreaRemoveSchema>;

export const serviceAreaListQuerySchema = listQuerySchema.extend({
  state: z.enum(US_STATE_CODES).optional(),
  zip: optionalText(5),
  active: queryBool,
  /** ZIP carrier routing: only ZIPs whose coverage list names this carrier. */
  carrierId: z
    .string()
    .regex(/^[a-f\d]{24}$/i, 'Invalid id')
    .optional()
    .or(z.literal('').transform(() => undefined)),
});

/**
 * ZIP carrier routing (owner, 2026-11-05): a carrier's coverage file. Add puts the carrier on the
 * listed ZIPs; Replace also takes it off every other ZIP; Remove takes it off the listed ZIPs.
 * Only ZIPs already in the service area change.
 */
export const COVERAGE_MODES = ['Add', 'Replace', 'Remove'] as const;
export type CoverageMode = (typeof COVERAGE_MODES)[number];
export const serviceAreaCoverageSchema = z.object({
  carrierId: z.string().regex(/^[a-f\d]{24}$/i, 'Select Carrier'),
  mode: z.enum(COVERAGE_MODES, { errorMap: () => ({ message: 'Select what to do' }) }),
});

/** One carrier and the ZIPs whose coverage list names it. */
export interface ServiceAreaCoverageRow {
  carrierId: string;
  code: string;
  name: string;
  zips: number;
}

export interface ServiceAreaCoverageResult {
  /** ZIPs of the file found in the service area. */
  matched: number;
  /** ZIPs of the file that are not in the service area (left alone). */
  notInArea: number;
  /** Replace: ZIPs the carrier was taken off. */
  removed: number;
}

export interface ServiceAreaDto {
  id: string;
  zip: string;
  city: string;
  state: string;
  active: boolean;
  tribal: TribalOption;
  programs: { lifeline: boolean; nonLifeline: boolean; ebb: boolean };
  /** ZIP carrier routing: the carriers that cover it; empty means every carrier. */
  carriers: Array<{ id: string; code: string }>;
  updatedAt: string;
}

/** One row of the "Area List": State, Active Zip, Inactive Zip. */
export interface ServiceAreaSummaryRow {
  state: string;
  activeZips: number;
  inactiveZips: number;
}

/** Result of checking a ZIP before an enrollment starts. */
export interface ZipCheckDto {
  zip: string;
  serviceable: boolean;
  state: string | null;
  city: string | null;
  /** Why the ZIP cannot be used, when it cannot. */
  reason: string | null;
}

/* ---------------------- Other Settings lists (PC240) ---------------------- */

/**
 * One entry of a managed list. The three lists share a shape; `display` is used by suspend
 * reasons and `notes` by note types (reference forms: appendix 11.5 of the product inventory).
 */
export const lookupInputSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255),
  status: recordStatus.default('Active'),
  /** Suspend Reason "Display": whether the reason is offered when an account is suspended. */
  display: z.boolean().default(true),
  /** Note type "Notes": the note text that goes with the type. Required for note types. */
  notes: z.string().trim().max(1000).default(''),
});
export type LookupInput = z.input<typeof lookupInputSchema>;
export const lookupUpdateSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255).optional(),
  status: recordStatus.optional(),
  display: z.boolean().optional(),
  notes: z.string().trim().max(1000).optional(),
});

const dayText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date')
  .optional();

export const lookupListQuerySchema = listQuerySchema.extend({
  q: optionalText(),
  status: recordStatus.optional(),
  /** Created date range (Manage Equipment Type), whole UTC days. */
  createdFrom: dayText,
  createdTo: dayText,
});

export interface LookupDto {
  id: string;
  /** Running number per list, shown as "Id" on Manage Note Type. */
  number: number;
  kind: LookupKind;
  name: string;
  status: RecordStatus;
  display: boolean;
  notes: string;
  createdAt: string;
  createdBy: UserRef | null;
  updatedAt: string;
  updatedBy: UserRef | null;
}

export interface LookupOption {
  id: string;
  name: string;
  /** Note types only: the note text that goes with the type. */
  notes?: string;
}
